import { IntelligencePersona } from '@@types/api.types';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { cn } from '@lib/utils';
import { INTELLIGENCE_PERSONA_OPTIONS } from '@utils/intelligence-persona.util';
import type { FC } from 'react';

const REMOVE_VALUE = 'REMOVE';

type IntelligencePersonaSelectProps = {
  className?: string;
  disabled?: boolean;
  // When provided, the dropdown shows a red "Remove" option that removes the member from the org.
  onRemove?: () => void;
  onPersonaChange: (persona: IntelligencePersona) => void;
  persona?: IntelligencePersona | null;
};

// AZ-451: persona selector mirroring the innovator "Manage Team" role dropdown — picking an option
// updates the persona; the red "Remove" option removes the member. Options carry permission
// descriptions so admins understand what each persona grants.
export const IntelligencePersonaSelect: FC<IntelligencePersonaSelectProps> = (props) => {
  const { className, disabled, onPersonaChange, onRemove, persona } = props;

  const handleValueChange = (value: string): void => {
    if (value === REMOVE_VALUE) {
      onRemove?.();
      return;
    }
    const nextPersona = INTELLIGENCE_PERSONA_OPTIONS.find((option) => option.value === value)?.value;
    if (nextPersona) {
      onPersonaChange(nextPersona);
    }
  };

  return (
    <Select
      disabled={disabled}
      onValueChange={handleValueChange}
      value={persona ?? undefined}
    >
      <SelectTrigger className={cn('h-10 border border-solid border-gray-200 bg-gray-100 p-3', className)}>
        <SelectValue placeholder="Select persona" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {onRemove && (
            <SelectItem
              className="text-red-500 focus:text-red-500"
              value={REMOVE_VALUE}
            >
              Remove
            </SelectItem>
          )}
          {INTELLIGENCE_PERSONA_OPTIONS.map((option) => (
            <SelectItem
              description={option.description}
              key={option.value}
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};
